"use client";

import { forwardRef, useCallback, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import NiceModal from "@ebay/nice-modal-react";
import { captureException } from "@sentry/nextjs";
import { Chain, WalletOption } from "@swapkit/core";
import { ChevronDown, ExternalLink } from "lucide-react";

import runeIcon from "~/assets/rune-icon-color.png";
import {
  Button,
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
} from "~/components/ui";
import { getShortWalletAddress, useSwapKit } from "~/context/swapkit";
import { formatPrice } from "~/helpers";
import { api } from "~/services/api";
import { cn, useBreakpoints } from "~/services/shadcn";
import { CopyWrapper, CopyWrapperIcon } from "../../common/CopyWrapper";
import { WalletGradient } from "../../common/WalletGradient";
import { ConnectKeystoreModal } from "../../modals";
import { supportedWallets } from "./supportedWallets";

export function NavbarConnectWalletPopover() {
  const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
  const swapkit = useSwapKit();
  const { data: priceByToken, isFetching: isFetchingRunePrice } = api.price.getByToken.useQuery();

  const { isWideEnough } = useBreakpoints();

  const walletAddress = swapkit?.connectedWallet?.address;

  const handleDisconnect = () => swapkit.disconnectWallet();

  const handleWalletConnection = useCallback(
    async (walletOption: WalletOption) => {
      try {
        // Handle KEYSTORE separately as exception
        if (walletOption === WalletOption.KEYSTORE) {
          const connectKeystoreModalData = await NiceModal.show<{ phrase: string }, never, never>(
            ConnectKeystoreModal,
          );

          await swapkit.connectWallet({
            walletOption: WalletOption.KEYSTORE,
            phrase: connectKeystoreModalData?.phrase,
          });

          // generate token based on timestamp

          return;
        }

        // Standard connection methods (other than KEYSTORE)
        await swapkit.connectWallet({ walletOption });
      } catch (error) {
        captureException("Could not connect the keystore wallet");
        return;
      }
    },
    [swapkit],
  );

  const isFetchingBalance = swapkit?.isConnected && !swapkit?.connectedWallet?.balance;
  const usdBalance =
    Number(swapkit?.connectedWallet?.balance || 0) * Number(priceByToken?.RUNE || 0);
  const connectedWalletDetails = supportedWallets.find(
    (wallet) => wallet.id === swapkit.connectedWallet?.walletType,
  );
  const WalletImage = connectedWalletDetails?.icon;

  const WalletButton = useMemo(
    () =>
      forwardRef<HTMLButtonElement>(function WalletButton(props, ref) {
        return (
          <div className="hidden shrink-0 overflow-hidden md:flex">
            {swapkit?.isConnected ? (
              <Button variant="outline" className="space-x-2 px-3" ref={ref} {...props}>
                <Skeleton active={!WalletImage} className="h-7 w-7">
                  {WalletImage && <WalletImage className="h-7 w-7 rounded-xs" />}
                </Skeleton>

                <div className="flex flex-1 flex-col">
                  <div className="text-xs tracking-wide text-white">
                    {getShortWalletAddress(walletAddress, { startLength: 8, endLength: 8 })}
                  </div>

                  <Skeleton
                    active={isFetchingBalance || isFetchingRunePrice}
                    className="h-3 w-[80px]"
                  >
                    <div className="flex items-center space-x-1 text-xs tracking-normal">
                      <div className="flex items-center space-x-1 text-white">
                        <Image alt="rune" src={runeIcon} className="w-3.5" />

                        <span>{swapkit?.connectedWallet?.balance}</span>
                      </div>

                      <div className="text-white/50">
                        {formatPrice(usdBalance, { asUSD: true, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  </Skeleton>
                </div>

                <ChevronDown className="w-4 shrink-0" />
              </Button>
            ) : (
              <Button ref={ref} {...props}>
                Connect Wallet
              </Button>
            )}
          </div>
        );
      }),
    [
      WalletImage,
      isFetchingBalance,
      usdBalance,
      swapkit?.isConnected,
      swapkit?.connectedWallet?.balance,
      isFetchingRunePrice,
      walletAddress,
    ],
  );

  return (
    <Popover onOpenChange={setIsWalletModalOpen} open={isWalletModalOpen}>
      <PopoverTrigger asChild>
        {isWideEnough ? (
          <WalletButton />
        ) : (
          <WalletGradient className="h-6 w-6 cursor-pointer md:hidden" />
        )}
      </PopoverTrigger>

      <PopoverContent
        animateOnClose={false}
        className={cn("flex w-[100vw] flex-col md:w-[360px] md:max-w-[480px]")}
        side="bottom"
        sideOffset={26}
        align="end"
      >
        <PopoverClose />

        <div className="mt-4 flex items-center border-b border-[#2D2226] px-2.5 pb-3 md:mt-6">
          <div className="text-lg font-medium uppercase">
            {swapkit?.isConnected ? "Your Wallet" : "Connect your Wallet"}
          </div>
        </div>

        <div className="mt-4 flex w-full flex-col px-2">
          {swapkit?.isConnected ? (
            <>
              <div className="flex items-center">
                <Link
                  target="_blank"
                  className="text-sm font-medium italic leading-none"
                  rel="noreferrer noopener"
                  href={swapkit?.client?.getExplorerAddressUrl({
                    chain: Chain.THORChain,
                    address: walletAddress || "",
                  })}
                >
                  <ExternalLink className="h-4 w-4" />
                </Link>

                <p className="px-2 text-sm font-medium leading-none">Address:</p>

                <CopyWrapper copyValue={walletAddress} tooltip={{ content: "Copy wallet address" }}>
                  <p className="ml-auto min-w-0 overflow-hidden text-ellipsis text-sm leading-none text-muted-foreground">
                    {walletAddress}
                  </p>

                  <CopyWrapperIcon className="w-5 fill-muted-foreground" />
                </CopyWrapper>
              </div>

              <div className="mt-4 flex items-center space-x-4">
                <Link
                  href="/profile"
                  className="w-full"
                  onClick={() => setIsWalletModalOpen(false)}
                >
                  <Button size="sm" className="w-full">
                    Profile
                  </Button>
                </Link>

                <Button variant="ghost" className="w-full" size="sm" onClick={handleDisconnect}>
                  Disconnect
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-col space-y-2">
              {supportedWallets.map((wallet) => {
                const WalletIcon = wallet.icon;

                return (
                  <Button
                    key={`connect-wallet-button-${wallet.id}`}
                    variant="wrapper"
                    onClick={() => handleWalletConnection(wallet.id)}
                    className="space-x-3 px-2 py-7"
                  >
                    <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xs bg-orange-gradient object-contain object-center">
                      <WalletIcon className={cn(wallet.id === WalletOption.KEYSTORE && "p-2")} />
                    </div>

                    <div className="font-semibold">{wallet.name}</div>

                    {/* {wallet?.isAvailable && (
                      <Badge className="!ml-auto">
                        Installed
                      </Badge>
                    )} */}
                  </Button>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
